import { LandmarkIcon } from 'lucide-react'

import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { CenteredLayout } from 'pages/employer_onboarding/CenteredLayout'
import { parseError } from 'pages/employer_onboarding/utils'
import { Badge, Banner, Button, ButtonGroup } from 'slab'
import { useGetEmployersBankingAccount as useGetBankingAccountQuery } from 'utils/web_api'

import { BankingSupportModal } from './BankingSupportModal'

/**
 * It seems that sometimes the Plaid account name includes the masked account
 * number, which we already display separately. This tries to detect that
 * and remove it from the name.
 */
function normalizedName (name: string) {
  if (!name) return ''

  return name.replace(/\.{3}\d{2,4}$/, '').trim()
}

export function ConnectedAccount ({ onBack, onNext }: { onBack: () => void, onNext: () => void }) {
  const { isLoading, data, error } = useGetBankingAccountQuery()

  function renderVerificationStatus () {
    switch (data?.verification_status) {
      case 'instantly_verified':
      case 'manually_verified':
      case 'automatically_verified':
        return <Badge type='success'>Verified</Badge>
      case 'unverified':
      case 'pending_automatic_verification':
      case 'pending_manual_verification':
        return <Badge type='info'>Pending</Badge>
      case 'verification_failed':
      case 'verification_expired':
        return <Badge type='critical'>Failed</Badge>
    }
  }

  function renderContent () {
    if (isLoading) {
      return (
        <div className='flex gap-4 items-center justify-between'>
          <div className='flex gap-2 items-center'>
            <LandmarkIcon size={24} className='text-gray-500' />
            <div>
              <SkeletonItem heightClass='h-4' widthClass='w-48' />
            </div>
          </div>
          <div>
            <SkeletonItem heightClass='h-4' widthClass='w-12' />
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <Banner type='critical' title='An error occurred loading your account information'>
          {parseError(error, '').message}
        </Banner>
      )
    }

    if (data) {
      return (
        <div className='flex gap-4 items-center justify-between'>
          <div className='flex gap-2 items-center'>
            {data.institution_logo
              ? (
                <div
                  className='size-10 shrink-0 bg-contain bg-center bg-no-repeat'
                  style={{
                    backgroundImage: `url(data:image/png;base64,${data.institution_logo})`,
                  }}
                />
                )
              : (
                <div className='size-10 shrink-0 bg-gray-200 rounded-full flex items-center justify-center'>
                  <LandmarkIcon size={20} className='text-gray-800' />
                </div>
                )}
            <div className='body'>
              {data.institution_name && (
                <div className='body-bold'>
                  {data.institution_name}{' '}
                </div>
              )}
              <div>
                {normalizedName(data.name)} ••••{data.mask}
              </div>
            </div>
          </div>
          <div>{renderVerificationStatus()}</div>
        </div>
      )
    }
  }

  return (
    <>
      <CenteredLayout.Container>
        {renderContent()}
      </CenteredLayout.Container>
      <div className='mt-10 flex items-center justify-between'>
        <BankingSupportModal />
        <ButtonGroup size='large'>
          <Button variant='secondary' onClick={onBack}>Back</Button>
          <Button variant='primary' onClick={onNext}>Next</Button>
        </ButtonGroup>
      </div>
    </>
  )
}
